import { useState } from 'react';
import { blogPosts, type BlogPost } from '../../../mocks/blog';

function ArticleModal({ post, onClose }: { post: BlogPost; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center p-4 pt-16 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-3xl w-full mb-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-full h-64 overflow-hidden rounded-t-2xl relative">
          <img src={post.image} alt={post.title} className="w-full h-full object-cover object-top" />
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center bg-black/40 hover:bg-black/60 text-white rounded-full transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
          <span className="absolute bottom-4 left-4 bg-green-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
            {post.category}
          </span>
        </div>
        <div className="p-8">
          <div className="flex items-center gap-3 mb-4 text-xs text-gray-400">
            <span className="flex items-center gap-1"><i className="ri-calendar-line"></i>{post.date}</span>
            <span className="w-1 h-1 rounded-full bg-gray-300"></span>
            <span className="flex items-center gap-1"><i className="ri-time-line"></i>{post.readTime}</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-6 leading-snug">{post.title}</h2>
          <div className="flex items-center gap-3 mb-8 pb-6 border-b border-gray-100">
            <div className="w-10 h-10 rounded-full overflow-hidden shrink-0">
              <img src={post.authorAvatar} alt={post.author} className="w-full h-full object-cover object-top" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">{post.author}</p>
              <p className="text-xs text-gray-400">{post.authorRole}</p>
            </div>
          </div>
          <div className="space-y-4 mb-8">
            {post.paragraphs.map((p, i) => (
              <p key={i} className="text-gray-600 leading-relaxed text-sm">{p}</p>
            ))}
          </div>
          {post.tips.length > 0 && (
            <div className="bg-green-50 border border-green-100 rounded-xl p-5 mb-8">
              <p className="text-sm font-bold text-green-700 mb-3 flex items-center gap-2">
                <i className="ri-lightbulb-line"></i>Key Takeaways
              </p>
              <ul className="space-y-2">
                {post.tips.map((tip, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                    <i className="ri-check-line text-green-500 mt-0.5 shrink-0"></i>{tip}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="flex flex-wrap gap-2 mb-8">
            {post.tags.map((tag) => (
              <span key={tag} className="bg-gray-100 text-gray-600 text-xs px-3 py-1 rounded-full">#{tag}</span>
            ))}
          </div>
          <div className="flex gap-3 pt-4 border-t border-gray-100">
            <a
              href="https://paystack.shop/pay/v9-4hu3n50"
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 rounded-xl font-semibold transition-colors cursor-pointer whitespace-nowrap text-sm flex items-center justify-center gap-2"
            >
              <i className="ri-shopping-bag-line"></i>Order Moringa Powder
            </a>
            <button
              onClick={onClose}
              className="px-6 border border-gray-200 hover:bg-gray-50 text-gray-600 py-3 rounded-xl font-semibold transition-colors cursor-pointer whitespace-nowrap text-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const dosageCards = [
  { icon: 'ri-user-line', label: 'Adults', dose: '1–2 tsp / day', detail: '3–6g · Start with ½ tsp', color: 'bg-green-600' },
  { icon: 'ri-parent-line', label: 'Children 2–5', dose: '¼ tsp / day', detail: '~1g · Mix into porridge', color: 'bg-teal-600' },
  { icon: 'ri-user-heart-line', label: 'Children 6–12', dose: '½ tsp / day', detail: '~1.5g · In smoothies', color: 'bg-emerald-600' },
  { icon: 'ri-heart-pulse-line', label: 'Elderly', dose: '1–2 tsp / day', detail: 'Same as adults', color: 'bg-green-700' },
];

const benefitStats = [
  { stat: '7×', label: 'more Vitamin C than oranges', icon: 'ri-sun-line' },
  { stat: '4×', label: 'more Calcium than milk', icon: 'ri-drop-line' },
  { stat: '3×', label: 'more Iron than spinach', icon: 'ri-shield-line' },
  { stat: '2 wks', label: 'to notice energy benefits', icon: 'ri-flashlight-line' },
];

export default function MoringaSpotlight() {
  const [open, setOpen] = useState(false);
  const article = blogPosts.find((p) => p.slug === 'moringa-powder-dosage-health-benefits-guide')!;

  return (
    <>
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Section label */}
          <div className="flex items-center gap-3 mb-10">
            <span className="inline-flex items-center gap-2 bg-green-100 text-green-700 text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-widest">
              <i className="ri-award-line"></i>Moringa Spotlight
            </span>
            <div className="flex-1 h-px bg-gray-100"></div>
          </div>

          {/* Main two-column layout */}
          <div className="grid lg:grid-cols-5 gap-10 items-start">

            {/* Left — Article card */}
            <div className="lg:col-span-3 group cursor-pointer" onClick={() => setOpen(true)}>
              <div className="rounded-2xl overflow-hidden border border-gray-100 relative">
                <div className="w-full h-72 overflow-hidden relative">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                  <div className="absolute bottom-4 left-4 flex items-center gap-2">
                    <span className="bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">
                      Featured Guide
                    </span>
                    <span className="bg-white/20 backdrop-blur-sm text-white text-xs px-3 py-1 rounded-full">
                      {article.readTime}
                    </span>
                  </div>
                </div>
                <div className="p-7">
                  <span className="text-xs font-semibold text-green-600 uppercase tracking-widest block mb-3">
                    {article.category} · {article.date}
                  </span>
                  <h2 className="text-2xl font-bold text-gray-900 leading-snug mb-4 group-hover:text-green-700 transition-colors">
                    {article.title}
                  </h2>
                  <p className="text-gray-500 text-sm leading-relaxed mb-6">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full overflow-hidden">
                        <img src={article.authorAvatar} alt={article.author} className="w-full h-full object-cover object-top" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-900">{article.author}</p>
                        <p className="text-xs text-gray-400">{article.authorRole}</p>
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors whitespace-nowrap">
                      Read Guide <i className="ri-arrow-right-line"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right — Dosage quick-reference + benefit stats */}
            <div className="lg:col-span-2 flex flex-col gap-6">

              {/* Dosage quick reference */}
              <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                <h3 className="font-bold text-gray-900 text-base mb-4 flex items-center gap-2">
                  <span className="w-7 h-7 flex items-center justify-center bg-green-100 rounded-full">
                    <i className="ri-medicine-bottle-line text-green-600 text-sm"></i>
                  </span>
                  Daily Dosage Quick Reference
                </h3>
                <div className="space-y-3">
                  {dosageCards.map((card, i) => (
                    <div key={i} className="flex items-center gap-3 bg-white rounded-xl px-4 py-3 border border-gray-100">
                      <span className={`w-9 h-9 flex items-center justify-center rounded-full ${card.color} flex-shrink-0`}>
                        <i className={`${card.icon} text-white text-sm`}></i>
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-bold text-gray-700">{card.label}</div>
                        <div className="text-xs text-gray-400">{card.detail}</div>
                      </div>
                      <span className="text-sm font-bold text-gray-900 whitespace-nowrap">{card.dose}</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-4 leading-relaxed">
                  Always introduce moringa gradually. Consult a doctor if you are pregnant or on medication.
                </p>
              </div>

              {/* Benefit stats */}
              <div className="bg-green-600 rounded-2xl p-6">
                <h3 className="font-bold text-white text-base mb-4">Why Moringa?</h3>
                <div className="grid grid-cols-2 gap-3">
                  {benefitStats.map((b, i) => (
                    <div key={i} className="bg-white/15 rounded-xl p-4 text-center">
                      <span className="w-8 h-8 flex items-center justify-center mx-auto mb-2 bg-white/20 rounded-full">
                        <i className={`${b.icon} text-white text-base`}></i>
                      </span>
                      <div className="text-2xl font-bold text-white leading-none">{b.stat}</div>
                      <div className="text-green-100 text-xs mt-1 leading-snug">{b.label}</div>
                    </div>
                  ))}
                </div>
                <a
                  href="https://paystack.shop/pay/v9-4hu3n50"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-5 w-full flex items-center justify-center gap-2 bg-white text-green-700 hover:bg-green-50 font-bold py-3 rounded-xl transition-colors cursor-pointer whitespace-nowrap text-sm"
                >
                  <i className="ri-shopping-bag-line"></i>
                  Order Moringa Powder
                </a>
              </div>

            </div>
          </div>

          {/* Week-by-week timeline strip */}
          <div className="mt-12 bg-gray-50 rounded-2xl p-8 border border-gray-100">
            <h3 className="font-bold text-gray-900 text-lg mb-6 text-center">
              What to Expect — Your Moringa Journey
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 relative">
              {[
                { week: 'Week 1–2', icon: 'ri-seedling-line', color: 'bg-green-100 text-green-600', title: 'Adjustment', desc: 'Your body adapts. Energy levels begin to stabilise and digestion improves.' },
                { week: 'Week 3–4', icon: 'ri-flashlight-line', color: 'bg-teal-100 text-teal-600', title: 'Energy Boost', desc: 'Noticeable improvement in daily energy, focus, and reduced mid-day fatigue.' },
                { week: 'Week 5–8', icon: 'ri-heart-line', color: 'bg-emerald-100 text-emerald-600', title: 'Deep Benefits', desc: 'Improved iron levels, stronger immunity, healthier skin, and better sleep quality.' },
                { week: 'Week 8+', icon: 'ri-award-line', color: 'bg-green-100 text-green-700', title: 'Full Wellness', desc: 'Consistent energy, stable blood sugar, strong bones — your long-term wellness foundation.' },
              ].map((step, i) => (
                <div key={i} className="relative flex flex-col items-center text-center">
                  {i < 3 && (
                    <div className="hidden sm:block absolute top-7 left-1/2 w-full h-0.5 bg-green-200 z-0"></div>
                  )}
                  <span className={`relative z-10 w-14 h-14 flex items-center justify-center rounded-full ${step.color} mb-3`}>
                    <i className={`${step.icon} text-2xl`}></i>
                  </span>
                  <span className="text-xs font-bold text-green-700 bg-green-50 px-3 py-0.5 rounded-full mb-2">{step.week}</span>
                  <h4 className="font-bold text-gray-900 text-sm mb-1">{step.title}</h4>
                  <p className="text-xs text-gray-500 leading-relaxed">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      {open && <ArticleModal post={article} onClose={() => setOpen(false)} />}
    </>
  );
}
